// ** React Imports
import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";
import md5 from "md5";

// ** Custom Components
import ProgressBar from "containers/Landing/containers/Register/components/ProgressBar/ProgressBar";
import Block from "components/layout/Block";
import { ButtonLanding } from "components/layout/Button/ButtonLanding";
import ErrorMessage from "containers/Landing/components/ErrorMessage";
import Text from "components/typography/Text";
import { StyledAuthPanelInputContainer } from "containers/Landing/styles/AuthPanelInputContainer.styled";
import ShowHidePasswordButton from "containers/Landing/components/ShowHidePasswordButton";
import StepLabel from "../../Register/components/StepLabel";

// ** Custom Hooks
import { useFingerprint } from "hooks/useFingerprint";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import { password as validatePassword } from "utils/validation";

// ** Styles Import
import { StyledAuthPanelInput } from "containers/Landing/styles/AuthPanelInput.styled";

// ** Config
import configGlobal from "configuration";
import { setIsAuth } from "store/modules/global-common.slice";

// #####################################################

const Step3 = ({ setActiveTab, currentStep }) => {
	const { t } = useTranslation("pages", "landing");

	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();

	const [recoverStep3Loading, setRecoverStep3Loading] = useState(false);
	const [password, setPassword] = useState("");
	const [passwordError, setPasswordError] = useState(false);
	const [passwordEmptyError, setPasswordEmptyError] = useState(false);
	const [showErrorMessageToLong, setShowErrorMessageToLong] = useState(false);
	const [passwordVisibility, setPasswordVisibility] = useState(false);

	const { getFingerprint, fingerprint } = useFingerprint();

	const isClient = useSelector((state) => state.globalCommon.isClient);

	let firebaseHash = null;

	try {
		firebaseHash =
			isClient && localStorage
				? localStorage.getItem(
						configGlobal.firebase.localStorage.itemName
				  )
				: null;
	} catch (e) {
		firebaseHash = null;
	}

	const activateFcmToken = (firebaseHashObject) => {
		const firebaseHash = firebaseHashObject
			? JSON.parse(firebaseHashObject)?.token
			: null;

		return api.put(`/setting/fcm/activate`, {
			id: null,
			hash: firebaseHash ? md5(firebaseHash) : undefined,
		});
	};

	const handleSubmitPassword = (event) => {
		event.preventDefault();
		setRecoverStep3Loading(true);

		getFingerprint().then(() => {
			if (password === "") {
				setPasswordEmptyError(true);
				setRecoverStep3Loading(false);
				return;
			}

			if (password.length > 64) {
				setShowErrorMessageToLong(true);
				setRecoverStep3Loading(false);
				return;
			}

			if (!validatePassword(password)) {
				setPasswordError(true);
				setRecoverStep3Loading(false);
				return;
			}

			setShowErrorMessageToLong(false);
			setPasswordError(false);

			if (!passwordError) {
				api.put("/authorization/remember/create/web", {
					password,
					id: location.state?.id,
					hash: location.state?.hash,
					chk: location.state?.chk,
					fingerprint: fingerprint,
				})
					.then((result) => {
						toast.success(t("landing:recover.recoverSuccess"));
						setPassword("");
						setActiveTab("login");

						const token =
							result.headers.authorization.match(
								/Bearer\s(\S+)/
							)[1];
						api.addAuthToken(token);
						if (firebaseHash) {
							activateFcmToken(firebaseHash);
						}
						dispatch(setIsAuth(true));
						history.push("/userpanel/data");
					})
					.catch((err) => {
						toast.error(err.response.data.error);
					})
					.finally(() => {
						setRecoverStep3Loading(false);
					});
			}
		});
	};

	// #####################################################

	return (
		<Block>
			<StepLabel label={t("landing:recover.stepDescriptions.header")} />
			<form onSubmit={handleSubmitPassword}>
				<Block mb={2}>
					<StyledAuthPanelInputContainer>
						<StyledAuthPanelInput
							type={passwordVisibility ? "text" : "password"}
							id="password"
							label="password"
							value={password}
							onChange={(event) => {
								setPassword(event.target.value);

								if (
									passwordError &&
									event.target.value === ""
								) {
									setPasswordError(false);
									setShowErrorMessageToLong(false);
								} else if (passwordError) {
									setPasswordError(
										!validatePassword(event.target.value)
									);
									setShowErrorMessageToLong(
										event.target.value.length > 64
									);
								}

								setPasswordEmptyError(
									event.target.value === ""
								);
							}}
							onBlur={(event) => {
								setPasswordError(
									event.target.value !== "" &&
										!validatePassword(event.target.value)
								);
								setShowErrorMessageToLong(
									event.target.value.length > 64
								);
								setPasswordEmptyError(
									event.target.value === ""
								);
							}}
							onFocus={() => {
								setPasswordError(false);
								setShowErrorMessageToLong(false);
							}}
							active={Boolean(password)}
							autoComplete="new-password"
							placeholder={t(
								"landing:register.passwordPlaceholder"
							)}
							register
						/>
						{password && password.length > 0 && (
							<ShowHidePasswordButton
								passwordVisibility={passwordVisibility}
								setPasswordVisibility={setPasswordVisibility}
							/>
						)}
					</StyledAuthPanelInputContainer>

					{passwordError && (
						<ErrorMessage>
							{t("landing:register.passwordError")}
						</ErrorMessage>
					)}
					{passwordEmptyError && (
						<ErrorMessage>
							{t("landing:register.passwordEmptyError")}
						</ErrorMessage>
					)}
					{!passwordError &&
						!passwordEmptyError &&
						showErrorMessageToLong && (
							<ErrorMessage>
								{t("landing:register.PasswordErrorTooLong")}
							</ErrorMessage>
						)}
				</Block>

				<Block mt={3}>
					<Text color="whiteAlways">
						{t(`landing:recover.stepDescriptions.${currentStep}`)}
					</Text>
				</Block>

				{/* Pasek Postępu */}
				<ProgressBar currentStep={3} totalSteps={4} />

				<ButtonLanding disabled={recoverStep3Loading} type="submit">
					{t("landing:recover.action")}
				</ButtonLanding>
			</form>
		</Block>
	);
};

// #####################################################

export default Step3;

// #####################################################
